import { useEffect, useState } from "react";
import { browserDateOnly } from "./helpers.js";

/**
 * When the reader's own day rolls over (#1450).
 *
 * A date-derived surface folds a date once — at render, or into a `?timezone=`
 * fetch — and then never asks again. That is right for every second of the day
 * except the one where the local date changes, and a PWA is routinely left open
 * across it: overnight on Finance or Value, or suspended and resumed the next
 * morning with no household event in between. The reader then keeps yesterday's
 * answer — an old `asOf`, a thing that became due at midnight still shown with a
 * month left, and on 1 January the previous year's horizon indefinitely.
 *
 * One shared signal rather than a patch per surface, subscribed the way
 * `addFeatureRefreshListener` is. Patching them individually is what lets them
 * drift apart again, which is the drift #1199 spent two review rounds removing.
 *
 * Deliberately NOT a re-render clock. It fires when the DATE STRING changes and
 * at no other time, so a surface can put it in a dependency array without
 * turning into a poller.
 */

type LocalDayListener = (day: string) => void;

/**
 * How often the day is re-read while the tab is being looked at.
 *
 * A poll rather than a timer aimed at midnight, on purpose. Aiming at midnight
 * means computing it, which means DST arithmetic (a local day is not always 24
 * hours), and a timer scheduled across a machine suspend does not fire when it
 * should anyway. Re-reading a formatted date is cheap, and being up to a minute
 * late to a day boundary is invisible on figures measured in months and years.
 */
const POLL_MS = 30_000;

const listeners = new Set<LocalDayListener>();
let currentDay = "";
let timer: ReturnType<typeof setInterval> | null = null;

/** The reader's today, as YYYY-MM-DD in their own zone. */
export function currentLocalDay(): string {
  return browserDateOnly();
}

function announceIfRolledOver(): void {
  const day = currentLocalDay();
  if (day === currentDay) return;
  currentDay = day;
  // A copy, so a listener that unsubscribes itself while being told does not
  // disturb the iteration.
  for (const listener of [...listeners]) listener(day);
}

function stopPolling(): void {
  if (timer === null) return;
  clearInterval(timer);
  timer = null;
}

/**
 * Poll only while the tab is visible.
 *
 * A hidden tab does not need to be told anything — nobody is reading it — and a
 * background timer that never stops is the part of a mechanism like this that
 * costs a phone battery. The `visibilitychange` handler is what makes the
 * suspend-and-resume case work regardless: coming back checks immediately,
 * which is the case a midnight timer cannot cover at all.
 */
function syncPolling(): void {
  if (!listeners.size || document.visibilityState === "hidden") {
    stopPolling();
    return;
  }
  if (timer === null) timer = setInterval(announceIfRolledOver, POLL_MS);
}

/**
 * Coming back: check, then make sure the timer is running again.
 *
 * One handler for all three resume events, because announcing and resuming the
 * poll are not separable (codex round 1). A tab that went hidden stopped its
 * timer; a `pageshow` from the back/forward cache that only announced would
 * catch the boundary it woke up on and then miss every one after it, which is
 * a worse failure than the one this module exists to fix — it looks fixed.
 */
function onResume(): void {
  if (document.visibilityState === "visible") announceIfRolledOver();
  syncPolling();
}

/**
 * Be told when the reader's local day changes. Returns an unsubscribe.
 *
 * `since` is the day the caller already believes, when it has one. A React
 * component samples the day while RENDERING and subscribes in an effect
 * afterwards, and those two moments can fall on either side of midnight
 * (codex round 1): the subscribe would then take today as the day to compare
 * against, the next poll would see no change, and that component would hold
 * yesterday until the NEXT midnight — a stale render that looks exactly like
 * the bug this module exists to remove. Passing what you sampled closes that
 * window; the listener is called straight away if the day has already moved.
 *
 * No-ops without a `window`, so a component that uses it still renders under
 * the static-markup test harness and under any server render.
 */
export function subscribeToLocalDay(listener: LocalDayListener, since?: string): () => void {
  if (typeof window === "undefined" || typeof document === "undefined") return () => undefined;
  if (!listeners.size) {
    currentDay = currentLocalDay();
    document.addEventListener("visibilitychange", onResume);
    // A tab restored from the back/forward cache does not fire
    // `visibilitychange`, and `pageshow` is the event that says it happened.
    window.addEventListener("pageshow", onResume);
    window.addEventListener("focus", onResume);
  }
  listeners.add(listener);
  syncPolling();
  const day = currentLocalDay();
  if (day !== currentDay) {
    // The MODULE is behind, not just this caller: the day moved and no poll
    // has run yet. Catching up through the shared path advances the baseline
    // and tells everyone once, including the listener just added — telling
    // only the new one would leave the rest to be told again by the next poll
    // (codex round 2).
    announceIfRolledOver();
  } else if (since !== undefined && since !== day) {
    // Only this caller is behind — its sample predates a rollover the module
    // has already recorded, or one this very call recorded as the first
    // subscriber. Nobody else needs telling, and the baseline is already right,
    // so the next poll stays silent instead of repeating this.
    listener(day);
  }
  return () => {
    listeners.delete(listener);
    if (!listeners.size) {
      stopPolling();
      document.removeEventListener("visibilitychange", onResume);
      window.removeEventListener("pageshow", onResume);
      window.removeEventListener("focus", onResume);
    }
  };
}

/**
 * The reader's today, re-rendering the caller when it changes.
 *
 * Use the returned value wherever `browserDateOnly()` was being called inline,
 * or put it in the dependency array of a fetch that sends `?timezone=` — either
 * way the surface re-derives at the day boundary instead of at the next
 * unrelated household event.
 */
export function useLocalDay(): string {
  const [day, setDay] = useState(currentLocalDay);
  // `day` is what THIS render sampled, and the effect below runs after it —
  // possibly on the other side of midnight. Handing it over is what lets the
  // subscribe notice and correct that; see `subscribeToLocalDay`.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: `day` is the sample being reconciled, not a trigger
  useEffect(() => subscribeToLocalDay(setDay, day), []);
  return day;
}

/** Test seam: drop shared state between cases. */
export function resetLocalDaySubscribersForTests(): void {
  listeners.clear();
  stopPolling();
  currentDay = "";
  if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onResume);
  if (typeof window !== "undefined") {
    window.removeEventListener("pageshow", onResume);
    window.removeEventListener("focus", onResume);
  }
}
